import React, { useState, useEffect } from 'react';
import { servicesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Stethoscope, FlaskConical, Syringe, Scan, Search, Download } from 'lucide-react';
import DataPagination from '../components/DataPagination';

const categoryIcons = {
  consultation: Stethoscope,
  lab: FlaskConical,
  procedure: Syringe,
  imaging: Scan,
  other: Stethoscope
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'consultation',
    is_active: true
  });

  useEffect(() => {
    fetchServices();
  }, [categoryFilter, currentPage, pageSize, searchQuery]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize };
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await servicesAPI.getAll(params);
      // Handle paginated response
      const paginatedData = response.data;
      if (paginatedData.items) {
        setServices(paginatedData.items);
        setTotalItems(paginatedData.total || 0);
        setTotalPages(paginatedData.total_pages || 0);
      } else {
        // Fallback for non-paginated response
        setServices(Array.isArray(paginatedData) ? paginatedData : []);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const exportServicesToExcel = async () => {
    setExportingExcel(true);
    try {
      const params = { page: 1, page_size: 500 };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await servicesAPI.getAll(params);
      const items = response.data.items || response.data || [];
      if (items.length === 0) {
        toast.error('No services to export');
        return;
      }
      const headers = ['Service Name', 'Category', 'Price', 'Status'];
      const rows = items.map(item => [
        item.name || '',
        item.category || '',
        item.price?.toFixed(2) || '0.00',
        item.is_active ? 'Active' : 'Inactive'
      ]);
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `services_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} services`);
    } catch (error) {
      toast.error('Failed to export services');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price)
      };
      
      if (editingService) {
        await servicesAPI.update(editingService.id, data);
        toast.success('Service updated successfully');
      } else {
        await servicesAPI.create(data);
        toast.success('Service created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this service?')) return;
    try {
      await servicesAPI.delete(id);
      toast.success('Service deactivated');
      fetchServices();
    } catch (error) {
      toast.error('Failed to deactivate service');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'consultation',
      is_active: true
    });
    setEditingService(null);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      price: service.price.toString(),
      category: service.category,
      is_active: service.is_active
    });
    setDialogOpen(true);
  };

  const getCategoryBadge = (category) => {
    const colors = {
      consultation: 'badge-info',
      lab: 'badge-warning',
      procedure: 'badge-error',
      imaging: 'badge-neutral',
      other: 'badge-neutral'
    };
    const Icon = categoryIcons[category] || Stethoscope;
    return (
      <span className={`${colors[category]} inline-flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {category}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="services-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Services</h1>
          <p className="text-slate-500 text-sm mt-1">Manage clinic services and pricing</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="pl-9 w-48 bg-white border-slate-200"
              data-testid="service-search"
            />
          </div>
          
          <Select value={categoryFilter} onValueChange={(val) => { setCategoryFilter(val); setCurrentPage(1); }}>
            <SelectTrigger className="w-56 bg-white border-slate-200" data-testid="category-filter">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="consultation">Consultation</SelectItem>
              <SelectItem value="lab">Lab</SelectItem>
              <SelectItem value="Labs">Labs</SelectItem>
              <SelectItem value="procedure">Procedure</SelectItem>
              <SelectItem value="imaging">Imaging</SelectItem>
              <SelectItem value="Injections and Procedures">Injections & Procedures</SelectItem>
              <SelectItem value="Panels">Panels</SelectItem>
              <SelectItem value="Weight Loss">Weight Loss</SelectItem>
              <SelectItem value="IV Therapy">IV Therapy</SelectItem>
              <SelectItem value="Ultrasounds">Ultrasounds</SelectItem>
              <SelectItem value="Immigration Package">Immigration Package</SelectItem>
              <SelectItem value="Adjustable Procedure">Adjustable Procedure</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={exportServicesToExcel}
            disabled={exportingExcel || services.length === 0}
            className="border-slate-200"
            data-testid="export-services-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            {exportingExcel ? 'Exporting...' : 'Export Excel'}
          </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="add-service-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                <DialogDescription>
                  {editingService ? 'Update service details' : 'Create a new service for your clinic'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">Service Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., General Consultation"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1.5"
                      data-testid="service-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description" className="text-slate-700">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Brief description of the service"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1.5"
                      data-testid="service-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price" className="text-slate-700">Price ($) *</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        className="mt-1.5"
                        data-testid="service-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category" className="text-slate-700">Category *</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(val) => setFormData({ ...formData, category: val })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="service-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consultation">Consultation</SelectItem>
                          <SelectItem value="lab">Lab</SelectItem>
                          <SelectItem value="Labs">Labs</SelectItem>
                          <SelectItem value="procedure">Procedure</SelectItem>
                          <SelectItem value="imaging">Imaging</SelectItem>
                          <SelectItem value="Injections and Procedures">Injections & Procedures</SelectItem>
                          <SelectItem value="Panels">Panels</SelectItem>
                          <SelectItem value="Weight Loss">Weight Loss</SelectItem>
                          <SelectItem value="IV Therapy">IV Therapy</SelectItem>
                          <SelectItem value="Ultrasounds">Ultrasounds</SelectItem>
                          <SelectItem value="Immigration Package">Immigration Package</SelectItem>
                          <SelectItem value="Adjustable Procedure">Adjustable Procedure</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active" className="text-slate-700">Active</Label>
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="save-service-btn">
                    {editingService ? 'Update Service' : 'Create Service'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Services Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : services.length === 0 ? (
            <div className="empty-state py-16">
              <Stethoscope className="empty-state-icon" />
              <p className="empty-state-title">No services found</p>
              <p className="empty-state-text">Add your first service to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-slate-600 font-medium">Name</TableHead>
                    <TableHead className="text-slate-600 font-medium">Description</TableHead>
                    <TableHead className="text-slate-600 font-medium">Category</TableHead>
                    <TableHead className="text-slate-600 font-medium">Price</TableHead>
                    <TableHead className="text-slate-600 font-medium">Status</TableHead>
                    <TableHead className="text-right text-slate-600 font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((service) => (
                    <TableRow key={service.id} className="hover:bg-slate-50" data-testid={`service-row-${service.id}`}>
                      <TableCell className="font-medium text-slate-900">{service.name}</TableCell>
                      <TableCell className="max-w-xs truncate text-slate-500">
                        {service.description || '-'}
                      </TableCell>
                      <TableCell>{getCategoryBadge(service.category)}</TableCell>
                      <TableCell className="font-mono text-slate-900">${service.price.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={service.is_active ? 'badge-success' : 'badge-error'}>
                          {service.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(service)}
                          className="text-slate-600 hover:text-slate-900"
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => handleDelete(service.id)}
                          data-testid={`delete-service-${service.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {!loading && services.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Services;
